import { inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, tap } from 'rxjs/operators';
import {
  INebulaDatabase,
  INebulaDrive,
  INebulaHistoryItem,
  INebulaHost,
  INebulaInfo,
  INebulaStore,
  ISecureDataRoomSetting,
  ISystemEnvironmentType
} from '@interfaces/nebula';
import { LoggerService } from './logger.service';
import { dateToLocal } from '@factories/helpers';

@Injectable({
  providedIn: 'root'
})
export class NebulaInfoService {
  private http = inject(HttpClient);
  private logger = inject(LoggerService);
  private baseUrl = inject<string>('BASE_URL' as never);

  readonly dateFormat = 'dd.MM.y - HH:mm';
  private apiBaseUrl: string;
  private apiBaseUrl1_3: string;

  constructor() {
    this.apiBaseUrl = `${this.baseUrl}api/info`;
    this.apiBaseUrl1_3 = `${this.baseUrl}api/v1.3`;
  }

  /**
   * get the latest server version
   */
  serverVersion(): Observable<INebulaInfo> {
    return this.http.get<INebulaInfo>(`${this.apiBaseUrl}/server-version`).pipe(
      tap({
        next: info => {
          info.timestamp = dateToLocal(info.timestamp, this.dateFormat);
        },
        error: error => {
          this.logger.error('Fehler beim Get AgiliCision Server Info', { error });
        }
      })
    );
  }

  /**
   * get environment
   */
  environment(): Observable<string> {
    return this.http.get<string>(`${this.apiBaseUrl}/environment`, { responseType: 'text' as 'json' }).pipe(
      tap({
        error: error => {
          this.logger.error('Fehler beim Get AgiliCision Environment Info', { error });
        }
      })
    );
  }

  /**
   * get the latest production server version
   */
  productVersion(): Observable<INebulaInfo> {
    return this.http.get<INebulaInfo>(`${this.apiBaseUrl}/product-version`).pipe(
      tap({
        next: info => {
          info.timestamp = dateToLocal(info.timestamp, this.dateFormat);
        },
        error: error => {
          this.logger.error('Fehler beim Get AgiliCision Product Version Info', { error });
        }
      })
    );
  }

  /**
   * get the latest webapp version
   */
  webAppVersion(): Observable<INebulaInfo> {
    return this.http.get<INebulaInfo>(`${this.apiBaseUrl}/web-app-version`).pipe(
      tap({
        next: info => {
          info.timestamp = dateToLocal(info.timestamp, this.dateFormat);
        },
        error: error => {
          this.logger.error('Fehler beim Get AgiliCision Web App Version Info', { error });
        }
      })
    );
  }

  /**
   * get the Secure Data Romm Setting
   */
  secureDataRoom(): Observable<ISecureDataRoomSetting> {
    return this.http.get<ISecureDataRoomSetting>(`${this.apiBaseUrl}/secure-data-room`).pipe(
      tap({
        error: error => {
          this.logger.error('Fehler beim Get den Sicherndatenraum Einstellung', { error });
        }
      })
    );
  }

  /**
   * gets the information about the host system.
   */
  host(): Observable<INebulaHost> {
    return this.http.get<INebulaHost>(`${this.apiBaseUrl1_3}/system-environment/host`).pipe(
      catchError(error => {
        this.logger.error('Fehler beim Get der Hostinformationen', { error });
        return throwError(() => error as Error);
      })
    );
  }

  /**
   * gets the information about the database system.
   * @param suppressDatabaseHistory Indicates whether to suppress writing the collected data to the database history.
   */
  database(suppressDatabaseHistory = true): Observable<INebulaDatabase> {
    const params = new HttpParams().set('suppressDatabaseHistory', suppressDatabaseHistory);
    return this.http.get<INebulaDatabase>(`${this.apiBaseUrl1_3}/system-environment/database`, { params }).pipe(
      catchError(error => {
        this.logger.error('Fehler beim Get der Datenbankinformationen', { error });
        return throwError(() => error as Error);
      })
    );
  }

  /**
   * gets the installation folder of the Nebula Server
   */
  path(): Observable<string> {
    return this.http
      .get(`${this.apiBaseUrl1_3}/system-environment/server/path`, {
        responseType: 'text'
      })
      .pipe(
        catchError(error => {
          this.logger.error('Fehler beim Get der Path', { error });
          return throwError(() => error as Error);
        })
      );
  }

  /**
   *gets the information about the installation drive of the Nebula Server.
   */
  drive(): Observable<INebulaDrive> {
    return this.http.get<INebulaDrive>(`${this.apiBaseUrl1_3}/system-environment/server/drive`).pipe(
      catchError(error => {
        this.logger.error('Fehler beim Get der Drive', { error });
        return throwError(() => error as Error);
      })
    );
  }

  /**
   * gets the sizes (in Bytes) of the stores (as defined in the appsettings).
   */
  store(): Observable<INebulaStore> {
    return this.http.get<INebulaStore>(`${this.apiBaseUrl1_3}/system-environment/store`).pipe(
      catchError(error => {
        this.logger.error('Fehler beim Get der Store', { error });
        return throwError(() => error as Error);
      })
    );
  }

  /**
   * gets the latest records of every system environment type from the history table of the database.
   */
  latestHistory(): Observable<INebulaHistoryItem[]> {
    return this.http.get<INebulaHistoryItem[]>(`${this.apiBaseUrl1_3}/system-environment/history/latest`).pipe(
      catchError(error => {
        this.logger.error('Fehler beim Get der History-Latest', { error });
        return throwError(() => error as Error);
      })
    );
  }

  /**
   * gets a list of supported system environment information types.
   */
  types(): Observable<ISystemEnvironmentType[]> {
    return this.http.get<ISystemEnvironmentType[]>(`${this.apiBaseUrl1_3}/system-environment/types`).pipe(
      catchError(error => {
        this.logger.error('Fehler beim Abruf der Typen von Systemumgebungsinformationen', { error });
        return throwError(() => error as Error);
      })
    );
  }
}
